import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicDonors } from '@/services/donors.api';
import { FrontendDonor } from '@/services/transform';

interface BannerItem {
  abbr: string;
  name: string;
  nameEn: string;
  color: string;
  logo?: string;
}

function MarqueeRow({ items, reverse, speed }: { items: BannerItem[]; reverse?: boolean; speed: number }) {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  // Duplicate for seamless loop
  const doubled = [...items, ...items, ...items];

  return (
    <div className="overflow-hidden w-full">
      <div
        className="flex items-center gap-4"
        style={{
          animation: `${reverse ? 'marqueeReverse' : 'marquee'} ${speed}s linear infinite`,
          width: 'max-content',
        }}
      >
        {doubled.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white border border-[#0A2540]/8 shrink-0 hover:border-[#0A2540]/20 transition-colors"
            style={{ minWidth: '180px' }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold shrink-0"
              style={{ backgroundColor: item.color }}
            >
              {item.abbr.length > 4 ? item.abbr.slice(0, 4) : item.abbr}
            </div>
            <span className="text-[#0A2540]/70 text-xs font-medium truncate">
              {isZh ? item.name : item.nameEn}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DonorsBanner() {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [donors, setDonors] = useState<FrontendDonor[]>([]);

  useEffect(() => {
    loadDonors();
  }, []);

  const loadDonors = async () => {
    try {
      const data = await getPublicDonors();
      setDonors(data || []);
    } catch (error) {
      console.error('加载捐赠人失败:', error);
      setDonors([]);
    }
  };

  // Convert donors to banner items
  const donorItems: BannerItem[] = donors.map((d) => ({
    abbr: d.abbr,
    name: d.name,
    nameEn: d.nameEn,
    color: d.color,
    logo: d.logo,
  }));

  // If no donors from API, hide the banner completely
  if (donorItems.length === 0) {
    return null;
  }

  // Split items for two rows
  const row1Items = donorItems.slice(0, Math.ceil(donorItems.length / 2));
  const row2Items = donorItems.slice(Math.ceil(donorItems.length / 2));

  // Double items for continuous scrolling
  const ROW1_ITEMS = [...row1Items, ...row1Items, ...row1Items];
  const ROW2_ITEMS = [...row2Items, ...row2Items, ...row2Items];

  return (
    <section className="py-12 bg-[#F7F9FC] overflow-hidden">
      <style>{`
        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes marqueeReverse {
          0%   { transform: translateX(-33.333%); }
          100% { transform: translateX(0); }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-[#0A2540]/8" />
          <div className="flex items-center gap-2 text-[#0A2540]/40 text-xs font-medium whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-star-line text-[#E8A838]" />
            </div>
            {isZh ? '感谢我们的全球支持伙伴' : 'Thank Our Global Supporters'}
          </div>
          <div className="h-px flex-1 bg-[#0A2540]/8" />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <MarqueeRow items={ROW1_ITEMS} speed={38} />
        <MarqueeRow items={ROW2_ITEMS} reverse speed={44} />
      </div>
    </section>
  );
}
